/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { ClassNames, css, jsx } from '@emotion/react';

import * as React from 'react';

import type { ValuePropItem } from '@coursera-web/cds-labs';
import { MODULE_LAYOUTS, ValuePropsModule } from '@coursera-web/cds-labs';

import { useLocation } from 'js/lib/useRouter';

import CareerOutcomesModule from 'bundles/degree-description/components/modules/career-outcomes/CareerOutcomesModule';
import FaqModule from 'bundles/degree-description/components/modules/faqs/FaqModule';
import HelpCenterBlock from 'bundles/degree-description/components/modules/faqs/HelpCenterBlock';
import AtAGlanceHorizontalBlock from 'bundles/degree-description/components/modules/hero/AtAGlanceHorizontalBlock';
import DeadlinesBlock from 'bundles/degree-description/components/modules/hero/DeadlinesBlock';
import EoiAtAGlanceBlock from 'bundles/degree-description/components/modules/hero/EoiAtAGlanceBlock';
import EoiButtonsBlock from 'bundles/degree-description/components/modules/hero/EoiButtonsBlock';
import HeroBanner from 'bundles/degree-description/components/modules/hero/HeroBanner';
import HeroModule from 'bundles/degree-description/components/modules/hero/HeroModule';
import InformationalSectionContainer from 'bundles/degree-description/components/modules/hero/InformationalSectionContainer';
import AdmissionRequirementsModule from 'bundles/degree-description/components/modules/program-details/AdmissionRequirementsModule';
import CurriculumModule from 'bundles/degree-description/components/modules/program-details/CurriculumModule';
import PayingForYourDegreeModule from 'bundles/degree-description/components/modules/program-details/PayingForYourDegreeModule';
import ProgramDetailsSection from 'bundles/degree-description/components/modules/program-details/ProgramDetailsSection';
import {
  getAdmissionRequirementsDummyContent,
  getCurriculumDummyContent,
  getFormatAndScheduleDummyContent,
  getPayingForYourDegreeDummyContent,
  getWhatYoullLearnDummyContent,
} from 'bundles/degree-description/components/modules/program-details/dummyContent';
import SchemaMarkup from 'bundles/degree-description/components/modules/structured-data-markup/SchemaMarkup';
import { HideOnDesktop, HideOnMobile } from 'bundles/degree-description/components/shared/BreakpointDisplay';
import ModuleContainerStyleWrap from 'bundles/degree-description/components/shared/ModuleContainerStyleWrap';
import RichText from 'bundles/degree-description/components/shared/RichText';
import type { PropsFromDegreePageContent } from 'bundles/degree-description/pages/consumer/degree-description-page/DegreePageContent';
import { PAGE_NAMES_MARKUP } from 'bundles/degree-description/utils/schema-markup';

import _t from 'i18n!nls/degree-description';

type Props = PropsFromDegreePageContent;

type ValuePropsTabContentProps = {
  heading: string;
  description: string;
  valueProps: ValuePropItem[];
};

const styles = {
  valuePropsTabCardClassName: css`
    padding: 0 !important;
    margin-top: var(--cds-spacing-200);
  `,
  zeroPaddingLayout: css`
    && {
      padding: 0;
    }
  `,
};

const ValuePropsTabContent = ({ heading, description, valueProps }: ValuePropsTabContentProps) => (
  <ClassNames>
    {({ css: cx }) => (
      <ValuePropsModule
        headline={heading}
        description={description}
        valueProps={valueProps}
        layout={MODULE_LAYOUTS.textTopImageBottom}
        cardBackgroundColor="transparent"
        cardClassName={cx(styles.valuePropsTabCardClassName)}
        customPageContainerStyles={[styles.zeroPaddingLayout]}
      />
    )}
  </ClassNames>
);

const Redesign2026SinglePage = ({ pageContent, eoiContainerText, eoiButtons, setEoiTrackingIndex }: Props) => {
  const { overviewAbout, backgroundImage, partner, programName, atAGlance, faqCollection, redesign2026Modules } =
    pageContent;

  const location = useLocation();

  // Dummy content standing in for the program details tabs until they're wired up to a real source.
  const whatYoullLearnDummyContent = getWhatYoullLearnDummyContent();
  const admissionRequirementsDummyContent = getAdmissionRequirementsDummyContent();
  const curriculumDummyContent = getCurriculumDummyContent();
  const formatAndScheduleDummyContent = getFormatAndScheduleDummyContent();
  const payingForYourDegreeDummyContent = getPayingForYourDegreeDummyContent();

  return (
    <div>
      <SchemaMarkup pageName={PAGE_NAMES_MARKUP.overview} pathName={location.pathname} pageContent={pageContent} />

      <HeroModule
        informationalSection={
          <HideOnMobile>
            <InformationalSectionContainer
              content={
                <RichText
                  content={overviewAbout?.json}
                  contentLinks={overviewAbout?.links}
                  linkTrackingName="overviewAbout_body_link"
                  buttonTrackingNameV3="degree_info_link"
                />
              }
              sidebarContent={
                <>
                  <DeadlinesBlock finalDeadlineInfo={atAGlance?.finalDeadlineInfo} />
                </>
              }
            />
          </HideOnMobile>
        }
        heroBanner={
          <HeroBanner
            backgroundImageUrl={backgroundImage?.url ?? ''}
            partnerLogoUrl={partner?.partnerLogo?.url ?? ''}
            partnerName={partner?.partnerName ?? ''}
            programName={programName ?? ''}
            isOverviewPage
            sidebarContent={
              <EoiButtonsBlock
                eoiContainerText={eoiContainerText}
                eoiButtons={eoiButtons}
                trackingIndex={1}
                setEoiTrackingIndex={setEoiTrackingIndex}
              />
            }
          />
        }
        contentInBetween={
          <>
            <AtAGlanceHorizontalBlock atAGlance={atAGlance} />
            <HideOnDesktop>
              <EoiAtAGlanceBlock atAGlance={atAGlance} trackingIndex={1} />
            </HideOnDesktop>
          </>
        }
      />

      {redesign2026Modules?.careerOutcomes && (
        <ModuleContainerStyleWrap>
          <CareerOutcomesModule
            heading={redesign2026Modules?.careerOutcomes?.heading}
            valueProps={redesign2026Modules?.careerOutcomes?.valuePropsCollection?.items}
            rolesTitle={redesign2026Modules?.careerOutcomes?.rolesHeading}
            roles={redesign2026Modules?.careerOutcomes?.rolesCollection?.items}
            logosTitle={redesign2026Modules?.careerOutcomes?.logosHeading}
            logos={redesign2026Modules?.careerOutcomes?.whereGraduatesWorkLogosCollection?.items}
          />
        </ModuleContainerStyleWrap>
      )}

      <ModuleContainerStyleWrap>
        <ProgramDetailsSection
          sectionHeading={_t('See the program details')}
          tabs={[
            {
              tabTitle: whatYoullLearnDummyContent.heading,
              tabContents: <ValuePropsTabContent {...whatYoullLearnDummyContent} />,
            },
            {
              tabTitle: admissionRequirementsDummyContent.heading,
              tabContents: <AdmissionRequirementsModule {...admissionRequirementsDummyContent} />,
            },
            {
              tabTitle: curriculumDummyContent.heading,
              tabContents: <CurriculumModule {...curriculumDummyContent} />,
            },
            {
              tabTitle: formatAndScheduleDummyContent.heading,
              tabContents: <ValuePropsTabContent {...formatAndScheduleDummyContent} />,
            },
            {
              tabTitle: payingForYourDegreeDummyContent.heading,
              tabContents: <PayingForYourDegreeModule {...payingForYourDegreeDummyContent} />,
            },
          ]}
        />
      </ModuleContainerStyleWrap>

      {faqCollection?.items && faqCollection.items.length > 0 && (
        <ModuleContainerStyleWrap>
          <FaqModule
            header={_t('Frequently asked questions')}
            faqItems={faqCollection.items}
            sideColumnContent={<HelpCenterBlock />}
          />
        </ModuleContainerStyleWrap>
      )}
    </div>
  );
};

export default Redesign2026SinglePage;
